import { useState, useEffect } from "react";
import axios from "axios";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import localize from "../../../common/localize";
import {
  PopperFullWidth,
  getInputLabelProps,
  getInputProps,
} from "../../../common/appStyles";
import {
  defaultNotBillingMotivation,
  getNotBillingMotivations,
} from "../../../common/EntitiesManagement";
import Combobox from "../../../components/common/Combobox";
import useGlobalContext from "../../../hooks/useGlobalContext";

export default function PaymentReport({ paymentModel, setPaymentModel }) {
  const { EVENTS_API, config } = useGlobalContext();
  const maximumCharactersForObservations = 1000;
  const [descriptionCount, setDescriptionCount] = useState(0);
  const [selectedMotivation, setSelectedMotivation] = useState(
    defaultNotBillingMotivation()
  );
  const motivationOptions = getNotBillingMotivations();
  const [paymentMethods, setPaymentMethods] = useState([]);

  const getPaymentMethods = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${EVENTS_API}/paymentMethod`, requestConfig);
    return data?.data?.Data;
  };

  const RequestPaymentMethods = async () => {
    const methods = await getPaymentMethods();

    if (methods && methods.length > 0) {
      setPaymentMethods(methods);
    }
  };

  const handlePaymentDateChange = (paymentDateValue) => {
    try {
      if (!paymentDateValue) {
        setPaymentModel({
          ...paymentModel,
          PaymentDate: new Date(),
        });
        return;
      }

      setPaymentModel({
        ...paymentModel,
        PaymentDate: paymentDateValue,
      });
    } catch {
      console.error("Invalid payment date");
    }
  };

  const handleMotivationChange = (value) => {
    if (!value) {
      setSelectedMotivation(defaultNotBillingMotivation());
      return;
    }

    setSelectedMotivation(value);
  };

  const handleObservationChange = (value) => {
    setPaymentModel({
      ...paymentModel,
      NoPaymentObservation: value.target.value,
    });
  };

  const handlePayedCheckedChanged = (value) => {
    setPaymentModel({
      ...paymentModel,
      Payed: value.target.checked,
      PaymentDate: !value.target.checked
        ? new Date()
        : paymentModel.PaymentDate,
    });
  };

  const handlePaymentMethodChange = (value) => {
    if (!value) {
      setPaymentModel({
        ...paymentModel,
        PaymentMethod: null,
        PaymentMethodId: 0,
      });
      return;
    }

    setPaymentModel({
      ...paymentModel,
      PaymentMethod: value,
      PaymentMethodId: value.Id,
    });
  };

  useEffect(() => {
    setPaymentModel({
      ...paymentModel,
      NoPaymentObservation:
        selectedMotivation.Id > 0 ? selectedMotivation.Name : "",
    });
  }, [selectedMotivation]);

  useEffect(() => {
    setDescriptionCount(paymentModel.NoPaymentObservation.length);
  }, [paymentModel?.NoPaymentObservation]);

  useEffect(() => {
    RequestPaymentMethods();
  }, []);

  return (
    <div style={{ width: "100%" }}>
      <div className="flex-col w-full mt-2 gap-4">
        <div>
          <FormControlLabel
            control={
              <Checkbox
                onChange={handlePayedCheckedChanged}
                checked={paymentModel.Payed}
              />
            }
            label={paymentModel.Payed ? localize.payed : localize.not_payed}
          />
        </div>
        {paymentModel.Payed ? (
          <>
            <div className="my-2">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={localize.payment_date}
                  className="w-full"
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  PopperComponent={PopperFullWidth}
                  value={paymentModel.PaymentDate}
                  onChange={handlePaymentDateChange}
                  maxDate={new Date()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "",
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        style: {
                          fontSize: "1em",
                          paddingBottom: "10px",
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </div>

            <div className="my-2">
              <Combobox
                isRequired
                id="PaymentMethodId"
                fieldLabel={localize.payment_method}
                fieldOptions={paymentMethods ?? []}
                fieldValue={paymentModel?.PaymentMethod}
                handleValueChange={handlePaymentMethodChange}
                popperFullWidth={PopperFullWidth}
              />
            </div>
          </>
        ) : (
          <div className="my-2">
            <Combobox
              isRequired
              fullWidth
              id="Motivation"
              fieldLabel={localize.motivation}
              fieldOptions={motivationOptions}
              fieldValue={selectedMotivation}
              handleValueChange={handleMotivationChange}
              popperFullWidth={PopperFullWidth}
            />

            {selectedMotivation.Id === 0 && (
              <div className="w-full mt-2">
                <FormControl required fullWidth>
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={{
                      style: {
                        fontSize: "0.8em",
                      },
                      maxLength: maximumCharactersForObservations,
                    }}
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    id="outlined-basic"
                    name="NoPaymentObservation"
                    label={localize.observations}
                    variant="outlined"
                    onChange={handleObservationChange}
                    value={paymentModel.NoPaymentObservation}
                    helperText={`${descriptionCount}/${maximumCharactersForObservations}`}
                  />
                </FormControl>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
